/**
 * Journal entries as narrow lines, shared by the Journal page and the
 * homepage. Hovering or focusing a line unfolds its cover; clicking anywhere
 * on it opens the entry. The newest is unfolded by default, so a phone (no
 * hover) always shows one cover and a tap opens.
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { JournalCard } from "../../ui/cards";
import { normalizeEntry } from "../../core/content";

/** The list of Journal entry lines; `entries` are normalized on the way in. */
const JournalList = ({ entries }) => {
	const items = entries.map((e) => ({
		...normalizeEntry("journal", e.slug, e),
		readingMinutes: e.readingMinutes || 1,
	}));
	const [active, setActive] = useState(items[0]?.slug ?? null);
	const list = useRef(null);

	// No hover on a phone: there, the line crossing the middle of the screen
	// unfolds as the visitor scrolls, and a tap opens it.
	useEffect(() => {
		if (!list.current || typeof IntersectionObserver === "undefined") return undefined;
		if (!window.matchMedia?.("(hover: none)").matches) return undefined;
		const io = new IntersectionObserver(
			(seen) => {
				const hit = seen.find((e) => e.isIntersecting);
				if (hit) setActive(/** @type {HTMLElement} */ (hit.target).dataset.slug);
			},
			{ rootMargin: "-45% 0px -45% 0px" },
		);
		list.current.querySelectorAll("[data-slug]").forEach((el) => io.observe(el));
		return () => io.disconnect();
	}, [items.length]);

	return (
		<div ref={list} className="border-t border-pp-rule">
			{items.map((entry, i) => (
				<div key={entry.slug} data-slug={entry.slug}>
					<JournalCard
						entry={entry}
						index={i}
						open={active === entry.slug}
						onActivate={() => setActive(entry.slug)}
					/>
				</div>
			))}
		</div>
	);
};

JournalList.propTypes = {
	entries: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string.isRequired })).isRequired,
};

export default JournalList;
